import React from "react";

import TableCell from "@mui/material/TableCell";
import MuiTableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";

import {
  SizeAndScroll,
  useElementSizeAndScroll,
  useId,
} from "@covertree/hooks";
import { slugify } from "@covertree/utils";
import { colors } from "@covertree/theme/colors";

import { TableActionMenu } from "../TableActionMenu";
import { Column, Row } from "../types";

interface TableRowProps {
  id: string;
  tableDimensions: SizeAndScroll;
  row: Row;
  columns: Column[];
  capitalize?: boolean;
  onRowActionClick?: () => void;
  onTableRowClick?: () => void;
  customCellFn?: (columnData: boolean) => JSX.Element;
}

export const TableRow = ({
  row,
  columns,
  id: rowId,
  tableDimensions,
  capitalize,
  onRowActionClick,
  onTableRowClick,
  customCellFn,
}: TableRowProps) => {
  const theme = useTheme();
  const [id, selector] = useId([rowId, row.id]);
  const rowDimensions = useElementSizeAndScroll(selector);

  return (
    <MuiTableRow
      id={id}
      sx={{
        cursor: "pointer",
        position: "relative",
        "&:hover": {
          backgroundColor: colors.orange4,
        },
        "&:last-child > td": {
          borderBottom: "none",
        },
        "& #action-button-wrapper": {
          visibility: "hidden",
        },
        "&:hover #action-button-wrapper": {
          visibility: "visible",
        },
      }}>
      {row.data.map((columnData, index) => {
        const isReactNode = React.isValidElement(columnData);
        return (
          <TableCell
            key={
              isReactNode
                ? `${rowId}-node-${index}`
                : `${rowId}-${columnData}-${slugify(columns[index].headerName)}`
            }
            sx={{
              borderBottom: "1px solid #eeeeee",
              boxShadow: "none",
              paddingBottom: theme.spacing(1.75),
              paddingTop: theme.spacing(1.75),
              "& > p:first-of-type": {
                borderRadius: theme.spacing(1, 0, 0, 1),
              },
              "&:last-child": {
                paddingRight: theme.spacing(),
                borderRadius: theme.spacing(0, 1, 1, 0),
              },
            }}
            onClick={(e) => {
              if (row.onClickRow) {
                row.onClickRow(e);
              }
              if (onTableRowClick) {
                onTableRowClick();
              }
            }}>
            {customCellFn && typeof columnData === "boolean" ? (
              customCellFn(columnData)
            ) : isReactNode ? (
              columnData
            ) : (
              <Typography
                noWrap
                display="block"
                textTransform={capitalize ? "capitalize" : undefined}>
                {columnData as string | number | boolean | null}
              </Typography>
            )}
          </TableCell>
        );
      })}
      {row.actionMenuItems !== undefined && (
        <TableActionMenu
          actionMenuItems={row.actionMenuItems}
          tableDimensions={tableDimensions}
          rowDimensions={rowDimensions}
          onRowActionClick={onRowActionClick}
        />
      )}
    </MuiTableRow>
  );
};
